<template>
  <div class="p-6">
    <h3 class="text-lg font-semibold mb-4 dark:text-white">
      Quick Post &mdash; {{ formattedDate }}
    </h3>

    <UiControl id="qc-keyword" label="Keyword">
      <UiInput v-model="form.keyword" placeholder="e.g. emergency plumber" size="sm" autofocus />
    </UiControl>

    <div class="grid grid-cols-2 gap-4">
      <UiControl id="qc-llm" label="LLM Provider">
        <UiSelect id="qc-llm" v-model="form.llm_provider" :options="llmOptions" size="sm" />
      </UiControl>

      <UiControl id="qc-image" label="Image Provider">
        <UiSelect id="qc-image" v-model="form.image_provider" :options="imageOptions" size="sm" />
      </UiControl>
    </div>

    <UiControl id="qc-post-type" label="Post Type">
      <UiRadioGroup id="qc-post-type" v-model="form.post_type" :options="postTypeOptions" position="horizontal" size="sm" />
    </UiControl>

    <UiControl id="qc-cta" label="CTA">
      <UiRadioGroup id="qc-cta" v-model="form.cta_type" :options="ctaOptions" position="horizontal" size="sm" />
    </UiControl>

    <div class="flex justify-end gap-3 mt-4 pt-4 border-t dark:border-gray-700">
      <button @click="$emit('on-close-panel')" class="btn btn-secondary btn-sm">
        Cancel
      </button>
      <button @click="submit" :disabled="submitting" class="btn btn-primary btn-sm">
        {{ submitting ? 'Creating...' : 'Generate Post' }}
      </button>
    </div>

    <p v-if="error" class="text-red-500 text-sm mt-3">
      {{ error }}
    </p>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { format } from 'date-fns'
import { createGbpCampaign } from '~/javascript/api/gbp_campaigns'
import UiInput from '~/javascript/components/ui/form/input/input.vue'
import UiSelect from '~/javascript/components/ui/form/select/select.vue'
import UiRadioGroup from '~/javascript/components/ui/form/radio/radio_group.vue'
import UiControl from '~/javascript/components/ui/form/control/control.vue'

const props = defineProps({
  locationId: { type: String, required: true },
  date: { type: Date, required: true }
})

const emit = defineEmits(['created', 'on-close-panel'])

const formattedDate = format(props.date, 'MMM d, yyyy')
const dateStr = format(props.date, 'yyyy-MM-dd')

const llmOptions = [
  { id: 'anthropic', title: 'Anthropic (Claude)' },
  { id: 'openai', title: 'OpenAI (GPT-4o)' },
  { id: 'gemini', title: 'Gemini' }
]

const imageOptions = [
  { id: 'gemini_images', title: 'Gemini Images' },
  { id: 'banana', title: 'Banana' }
]

const postTypeOptions = [
  { value: 'update', label: 'Update' },
  { value: 'event', label: 'Event' }
]

const ctaOptions = [
  { value: 'call_now', label: 'Call Now' },
  { value: 'learn_more', label: 'Learn More' }
]

const form = reactive({
  keyword: '',
  llm_provider: 'anthropic',
  image_provider: 'gemini_images',
  post_type: 'update',
  cta_type: 'learn_more'
})

const submitting = ref(false)
const error = ref(null)

async function submit() {
  if (!form.keyword.trim()) {
    error.value = 'Keyword is required'
    return
  }
  submitting.value = true
  error.value = null
  try {
    const campaignData = {
      gbp_campaign: {
        name: `${form.keyword.trim()} - ${formattedDate}`,
        keywords: [form.keyword.trim()],
        date_range_start: dateStr,
        date_range_end: dateStr,
        target_posts_count: 1,
        post_type: form.post_type,
        cta_type: form.cta_type,
        llm_provider: form.llm_provider,
        image_provider: form.image_provider,
        utm_params: { source: 'google', medium: 'organic', campaign: 'gbp-posts' }
      }
    }
    const { data } = await createGbpCampaign(props.locationId, campaignData)
    emit('created', data)
  } catch (e) {
    error.value = e.response?.data?.error || 'Failed to create post'
  } finally {
    submitting.value = false
  }
}
</script>
